import React, { useEffect, useState } from "react";
import { getSortedCollection, matchQuery } from "sprint-common-web";

import { useMuxSpecificationApi } from "../../../common/api/orders-api/mux-specification/useMuxSpecificationApi";
import { MuxerType } from "../../../common/types/MuxerType";
import { MuxSpecificationListingItem, mapToMuxSpecificationListingItem } from "../listing/MuxSpecificationListingItem";
import { MuxSpecificationListingFilters } from "../listing/useMuxSpecificationListingFilters";
import { MuxSpecificationListingContext } from "./MuxSpecificationListingContext";

export type MuxSpecificationListingProviderProps = React.PropsWithChildren<unknown>;

export const MuxSpecificationListingProvider: React.FC = ({ children }) => {
    const [allItems, setAllItems] = useState<MuxSpecificationListingItem[]>([]);
    const [items, setItems] = useState<MuxSpecificationListingItem[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const { getMuxSpecificationList } = useMuxSpecificationApi();

    useEffect(() => {
        setIsLoading(true);
        getMuxSpecificationList()
            .then((response) => {
                const muxSpecificationItems = response.items.map(mapToMuxSpecificationListingItem);

                setAllItems(muxSpecificationItems);
                setTotalItems(muxSpecificationItems.length);
            })
            .finally(() => setIsLoading(false));
    }, []);

    const load = (filters: MuxSpecificationListingFilters): void => {
        const sortedItems = getSortedAndFilteredItems(allItems, filters);

        const modifiedItems = sortedItems.filter((item) => item.isModified === true);

        setTotalItems(sortedItems.length);
        setItems(Array.from(new Set([...modifiedItems, ...sortedItems])));
    };

    const addMuxSpecificationItem = (item: MuxSpecificationListingItem): void => {
        setAllItems((prevItems) => {
            return [
                {
                    id: item.id,
                    name: item.name,
                    type: item.type,
                    details: item.details,
                    encodeSpecificationType: item.encodeSpecificationType,
                    updatedDate: new Date().toISOString(),
                    isModified: true
                },
                ...prevItems
            ];
        });
    };

    const updateMuxSpecificationItem = (item: MuxSpecificationListingItem): void => {
        setAllItems((prevItems) => {
            const muxSpecificationToUpdate = prevItems.find((framerateProfile) => framerateProfile.id === item.id);

            if (!muxSpecificationToUpdate) return prevItems;

            muxSpecificationToUpdate.name = item.name;
            muxSpecificationToUpdate.type = item.type;
            muxSpecificationToUpdate.details = item.details;
            muxSpecificationToUpdate.updatedDate = item.updatedDate;
            muxSpecificationToUpdate.isModified = true;

            return [...prevItems];
        });
    };

    return (
        <MuxSpecificationListingContext.Provider
            value={{
                isLoading,
                totalItems,
                allItems,
                items,
                add: addMuxSpecificationItem,
                update: updateMuxSpecificationItem,
                load
            }}
        >
            {children}
        </MuxSpecificationListingContext.Provider>
    );
};

const getSortedAndFilteredItems = (items: MuxSpecificationListingItem[], filters: MuxSpecificationListingFilters) => {
    const sortedItems = getSortedCollection(items, filters.sortBy, filters.sortOrder);

    if (filters.typeId) {
        const filterType = MuxerType[filters.typeId as keyof typeof MuxerType];

        return filterItems(
            sortedItems.filter((item) => item.type === filterType),
            filters.name ?? ""
        );
    }

    return filterItems(sortedItems, filters.name ?? "");
};

const filterItems = (items: MuxSpecificationListingItem[], name: string) =>
    items.filter((item) => matchQuery(item.name, name));
